import translations from '../localization/translations';

const USER_TYPE_KEYS = {
  dentist: 'userTypes.dentist',
  technician: 'userTypes.technician',
  courier: 'userTypes.courier',
  admin: 'userTypes.admin',
  accountant: 'userTypes.accountant',
  designer: 'userTypes.designer',
  scanner: 'userTypes.scanner',
};

/** Lab-defined roles have generated names (custom_…) and carry their own label. */
export const CUSTOM_USER_TYPE_PREFIX = 'custom_';

const customUserTypeLabels = new Map();

export const isCustomUserTypeName = (name) =>
  String(name ?? '').trim().toLowerCase().startsWith(CUSTOM_USER_TYPE_PREFIX);

/** Staff types are every lab user type except client (dentist) accounts. */
export const isStaffUserType = (type) => {
  const name = String(type?.name ?? '').trim().toLowerCase();
  return name !== '' && name !== 'dentist' && name !== 'backoffice';
};

/**
 * Remember custom role labels so translateUserTypeName can show them.
 * Accepts a list of user types ({ name, label, label_ar }) or a { name: label } map.
 */
export const registerUserTypeLabels = (types) => {
  const entries = Array.isArray(types)
    ? types.map((type) => [type?.name, type?.label, type?.label_ar])
    : Object.entries(types ?? {});
  entries.forEach(([name, label, labelAr]) => {
    if (name && label) {
      customUserTypeLabels.set(String(name).trim().toLowerCase(), {
        label: String(label),
        labelAr: labelAr ? String(labelAr) : null,
      });
    }
  });
};

const currentLanguage = () =>
  (typeof document !== 'undefined' && document.documentElement.getAttribute('lang')) || 'ar';

/** A custom role's name in the given language; Arabic falls back to the main label when unset. */
export const localizedUserTypeLabel = (type, language = currentLanguage()) => {
  if (!type?.label) return null;
  return language === 'ar' && type.label_ar ? type.label_ar : type.label;
};

const registeredLabel = (slug, language) => {
  const entry = customUserTypeLabels.get(slug);
  if (!entry) return null;
  return language === 'ar' && entry.labelAr ? entry.labelAr : entry.label;
};

const DELIVERY_TYPE_SLUG_KEYS = {
  'standard-delivery': 'deliveryTypes.standard_delivery',
  'free-delivery': 'deliveryTypes.free_delivery',
};

const lookupTranslation = (key, language, fallback) =>
  translations[language]?.[key] ?? translations.en?.[key] ?? fallback;

export const translateUserTypeName = (name, language = 'ar', clientType = null) => {
  if (name === null || name === undefined || name === '') {
    return name ?? '';
  }

  const slug = String(name).trim().toLowerCase();
  const isLabClient = String(clientType ?? '').trim().toLowerCase() === 'lab';

  // Same user_type as dentist; lab (B2B) clients display as "Lab contact".
  if (slug === 'dentist' && isLabClient) {
    return lookupTranslation('userTypes.lab_contact', language, 'Lab contact');
  }

  const key = USER_TYPE_KEYS[slug];
  if (key) {
    return lookupTranslation(key, language, name);
  }

  const custom = registeredLabel(slug, language);
  if (custom) {
    return custom;
  }

  return isCustomUserTypeName(slug) ? lookupTranslation('userTypes.custom', language, 'Custom role') : name;
};

export const customUserTypeLabel = (name, language = currentLanguage()) =>
  registeredLabel(String(name ?? '').trim().toLowerCase(), language);

/** Display name for a user type object, preferring a custom role's own label. */
export const userTypeDisplayName = (type, language = 'ar', clientType = null) => {
  if (!type) return '';
  if (type.label) {
    registerUserTypeLabels([type]);
    return localizedUserTypeLabel(type, language);
  }
  return translateUserTypeName(type.name, language, clientType);
};

export const translateDeliveryTypeName = (value, language = 'ar') => {
  if (value === null || value === undefined || value === '') {
    return value ?? '';
  }

  let name = null;
  let slug = null;

  if (typeof value === 'string') {
    name = value;
  } else if (typeof value === 'object') {
    name = value.name ?? null;
    slug = value.slug ?? null;
  }

  const slugCandidates = [];
  if (slug) {
    slugCandidates.push(String(slug).trim().toLowerCase());
  }
  if (name) {
    slugCandidates.push(String(name).trim().toLowerCase().replace(/\s+/g, '-'));
  }

  for (const candidate of slugCandidates) {
    const key = DELIVERY_TYPE_SLUG_KEYS[candidate];
    if (key) {
      return lookupTranslation(key, language, name ?? slug);
    }
  }

  return name ?? slug ?? value;
};

export const SYSTEM_EXPENSE_TYPE_CODES = ['materials', 'technicians', 'additional_operation_cost'];

const SYSTEM_EXPENSE_TYPE_NAMES = {
  materials: ['Dental Materials', 'خامات الأسنان', 'Materials', 'خامات'],
  technicians: ['Dental Technicians', 'فنيي الأسنان', 'Technicians', 'فنيين'],
  additional_operation_cost: ['Additional Operation Cost', 'تكاليف تشغيلية إضافية'],
};

const systemExpenseTypeCode = (value) => {
  if (value && typeof value === 'object') {
    return SYSTEM_EXPENSE_TYPE_CODES.includes(value.code) ? value.code : null;
  }
  if (typeof value === 'string') {
    const name = value.trim().toLowerCase();
    return Object.keys(SYSTEM_EXPENSE_TYPE_NAMES).find((code) =>
      SYSTEM_EXPENSE_TYPE_NAMES[code].some((candidate) => candidate.toLowerCase() === name)
    ) ?? null;
  }
  return null;
};

/** Default expense types (by `code`) are locked: they cannot be edited or deleted. */
export const isSystemExpenseType = (expenseType) =>
  Boolean(expenseType && typeof expenseType === 'object' && (expenseType.is_system || systemExpenseTypeCode(expenseType)));

/** Accepts an expense type object ({ code, name }) or a bare name. */
export const translateExpenseTypeName = (value, language = 'ar') => {
  const name = value && typeof value === 'object' ? value.name : value;
  const code = systemExpenseTypeCode(value);
  return code ? lookupTranslation(`expenseTypes.system.${code}.name`, language, name ?? code) : (name ?? '');
};

export const translateExpenseTypeDescription = (expenseType, language = 'ar') => {
  const description = expenseType?.description ?? '';
  const code = systemExpenseTypeCode(expenseType);
  return code ? lookupTranslation(`expenseTypes.system.${code}.description`, language, description) : description;
};

export const getDateRangePlaceholder = (language = 'ar') => [
  lookupTranslation('ui.date.startDate', language, 'Start Date'),
  lookupTranslation('ui.date.endDate', language, 'End Date'),
];
